import { appPodContainerLastStateSchema } from "./app-pods.js";
import { appEventTypeSchema } from "./app-events.js";
import { z } from "zod";
//#region src/dp-k8s.ts
/**
* The response bodies of the three platform-admin k8s reads of one data plane:
* `GET /v1/admin/data-planes/:id/k8s/pods`, `/k8s/events` and `/k8s/resources` (site-admin-api
* `DpAdminController`, gate `cp:dp:read`).
*
* Plan `2026-10-21-operator-tui`, defaults row F1: a route response gets a strict schema.
*
* Producer: provisioning-svc `KubernetesService.listPods`, `listEvents` and `getResources`. Each
* maps the DP's REST answer onto a legacy shape (`mapRestPodToLegacy`, `mapRestEventToLegacy`,
* `aggregatePodSpecResources`). site-admin-api `DpAdminService.getK8sPods`, `getK8sEvents` and
* `getK8sResources` return that body unchanged. These schemas restate the legacy shapes, field for
* field: `KubePodInfo`, `KubeContainerInfo`, `KubeEventInfo` and `PodResourceAggregate`.
*
* These are the namespace-wide reads. The app-scoped reads have their own shapes,
* `appPodsResponseSchema` and `appEventsResponseSchema`; the two audiences keep two shapes on purpose.
*/
/**
* The label attribution of one pod: only the four keys `distillAttribution` keeps, each a non-empty
* string when present. An empty object means the DP sent an attribution with no usable key.
*/
const dpK8sPodAttributionSchema = z.object({
	app: z.string().min(1).optional(),
	fixture: z.string().min(1).optional(),
	version: z.string().min(1).optional(),
	serviceName: z.string().min(1).optional()
}).strict();
/**
* One container of a pod.
*
* `status` is the container state under its CP name (`running`, `waiting`, `terminated`), or the
* pod phase when the DP sent no state. `name` is `''` when the DP sent no name. `reason` is absent
* for a healthy running container. `exitCode` `0` is a clean exit. `lastState` is absent when the
* container never restarted.
*/
const dpK8sContainerSchema = z.object({
	name: z.string(),
	status: z.string(),
	ready: z.boolean(),
	restartCount: z.number().int().nonnegative(),
	image: z.string().optional(),
	reason: z.string().min(1).optional(),
	exitCode: z.number().int().optional(),
	lastState: appPodContainerLastStateSchema.optional()
}).strict();
/**
* One pod of the data plane namespace.
*
* - `status`: the pod phase as the DP sent it.
* - `ready`: true when the pod has at least one container and every container is ready.
* - `restarts`: the sum of `restartCount` over the containers.
* - `ageSeconds`, `attribution`, `nodeName`: absent when the DP did not send them. An absent
*   `nodeName` on a pending pod is the signal that it has no node.
* - `unschedulable`: absent means the DP did not report it, which is not `false`.
*/
const dpK8sPodSchema = z.object({
	name: z.string().min(1),
	status: z.string(),
	ready: z.boolean(),
	restarts: z.number().int().nonnegative(),
	ageSeconds: z.number().nonnegative().optional(),
	attribution: dpK8sPodAttributionSchema.optional(),
	nodeName: z.string().min(1).optional(),
	unschedulable: z.boolean().optional(),
	containers: z.array(dpK8sContainerSchema)
}).strict();
/** `GET /v1/admin/data-planes/:id/k8s/pods`. `fetchedAt` is the CP's ISO-8601 read time. */
const dpK8sPodsResponseSchema = z.object({
	pods: z.array(dpK8sPodSchema),
	namespace: z.string().min(1),
	fetchedAt: z.iso.datetime()
}).strict();
/**
* One k8s event of the namespace.
*
* - `type`: `Warning`, or `Normal` for every other type the DP sent.
* - `involvedObject`: `<Kind>/<name>` as one string.
* - `ageSeconds`: whole seconds since `lastTimestamp`, `0` when the DP sent none.
* - `firstTimestamp`, `lastTimestamp`: the DP's text, `''` when the DP sent `null`.
*/
const dpK8sEventSchema = z.object({
	type: appEventTypeSchema,
	reason: z.string(),
	message: z.string(),
	involvedObject: z.string(),
	ageSeconds: z.number().int().nonnegative(),
	count: z.number().int().nonnegative(),
	firstTimestamp: z.string(),
	lastTimestamp: z.string()
}).strict();
/** `GET /v1/admin/data-planes/:id/k8s/events`. */
const dpK8sEventsResponseSchema = z.object({
	events: z.array(dpK8sEventSchema),
	namespace: z.string().min(1),
	fetchedAt: z.iso.datetime()
}).strict();
/** Why the usage fields are `null`: no live pod to measure, or nothing measures usage. */
const DP_K8S_USAGE_UNAVAILABLE_REASONS = ["no-pods", "usage-not-collected"];
/**
* `GET /v1/admin/data-planes/:id/k8s/resources`: the declared CPU and memory of the live pods.
*
* These are requests and limits, never usage. `metricsAvailable` is always `false` and each
* `usage*` field is always `null`. `cpu` and `memory` are `null` when `usageUnavailableReason` is
* `no-pods`. `podsCounted` stops at 5000 (`MAX_PODS` of the producer); at that value the sums
* describe the first 5000 pods only.
*/
const dpK8sResourcesResponseSchema = z.object({
	namespace: z.string().min(1),
	cpu: z.object({
		requestMillicores: z.number().nonnegative(),
		limitMillicores: z.number().nonnegative(),
		usageMillicores: z.null()
	}).strict().nullable(),
	memory: z.object({
		requestBytes: z.number().nonnegative(),
		limitBytes: z.number().nonnegative(),
		usageBytes: z.null()
	}).strict().nullable(),
	metricsAvailable: z.literal(false),
	usageUnavailableReason: z.enum(DP_K8S_USAGE_UNAVAILABLE_REASONS),
	podsCounted: z.number().int().nonnegative(),
	podsWithoutResourceSpec: z.number().int().nonnegative(),
	fetchedAt: z.iso.datetime()
}).strict();
//#endregion
export { DP_K8S_USAGE_UNAVAILABLE_REASONS, dpK8sContainerSchema, dpK8sEventSchema, dpK8sEventsResponseSchema, dpK8sPodAttributionSchema, dpK8sPodSchema, dpK8sPodsResponseSchema, dpK8sResourcesResponseSchema };

//# sourceMappingURL=dp-k8s.js.map